import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import { mdiCalendarClockOutline, mdiCalendarQuestionOutline } from "@mdi/js"
import { buildPropertyObjectTagPresentation } from "../../../../../../../../../../../../../_shared/_logic/object-tags"
import { buildAppointmentSubLabel } from "../../../../../../../../../../../../../../../_shared/_logic/appointment-contact"
import {
  buildAppointmentTimeTagPresentation,
  type AppointmentTimeTagInput,
} from "../../../../../../../../../../../../../../../_shared/_logic/appointment-time-tags"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../../../../../../../../../../_shared/_logic/communication-presentation"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../../../../../../../../../_shared/_logic/rich-tooltips"
import { internalOrderTypeLabel } from "../../../../../../../../../../../../../../../_shared/_logic/order-type-labels"
import { formatWorkOrderDueDate } from "../../../../../../../../../../../../../_shared/_logic/summary-tag"
import type {
  AssignWorkOrderDraft,
  WorkOrderAssignmentCandidate,
  WorkOrderAssignmentRow,
  WorkOrderAssignmentSelection,
} from "../../../contract"
import { recommendedWorkOrderCandidates } from "../recommendations"

export type WorkOrderAssignmentOriginSource =
  | "customer"
  | "case_context"
  | "preconfiguration"
  | "manual"
  | "work_order"
  | "requester_appointment"
  | "requester_selection"
  | "none"

export type WorkOrderAssignmentFieldOriginMeta = {
  initialValue: string
  initialSource: WorkOrderAssignmentOriginSource
  currentSource: WorkOrderAssignmentOriginSource
}

const normalizeText = (value: unknown) => `${value ?? ""}`.trim()
const firstText = (...values: unknown[]) => {
  for (const value of values) {
    const normalized = normalizeText(value)

    if (normalized) return normalized
  }

  return ""
}
const normalizeWhitespace = (value: string) => value.replace(/\s+/g, " ").trim()
const compactOrderTypeLabel = (value: string) => {
  const normalized = value.trim()

  if (normalized.length <= 35) return normalized

  return `${normalized.slice(0, 35).trimEnd()}...`
}
const orderNumberLabel = (value: string) => {
  const normalized = normalizeText(value).replace(/^#/, "")

  return normalized ? `#${normalized}` : ""
}

const selectedOverride = (
  row: WorkOrderAssignmentRow,
  draft: AssignWorkOrderDraft,
): WorkOrderAssignmentSelection | null => {
  const rowWorkOrderId = normalizeText(row.workOrderId)
  const draftWorkOrderId = normalizeText(draft.workOrderId)

  if (!draft.selectedWorkOrder || !draftWorkOrderId || draftWorkOrderId === rowWorkOrderId)
    return null

  return draft.selectedWorkOrder
}

const numberValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)

    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }

  return null
}

const objectValue = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const parseDate = (value: unknown): Date | null => {
  const text = normalizeText(value)

  if (!text) return null

  const parsed = new Date(text)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const scheduleStart = (row: WorkOrderAssignmentRow) => {
  const slot = objectValue(row.payload.slot)

  return parseDate(
    row.appointmentTime?.start_at ??
      row.payload.schedule_start_at ??
      row.payload.slot_start_at ??
      row.payload.slot_start ??
      slot.start_at,
  )
}

const formatDate = (value: Date) =>
  value.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Europe/Berlin",
  })

const formatTime = (value: Date) =>
  value.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  })

const formatScheduleRange = (start: Date, end: Date) =>
  `${formatDate(start)} ${formatTime(start)} - ${formatTime(end)}`

const BERLIN_TIME_ZONE = "Europe/Berlin"
const berlinDateTimePartsFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: BERLIN_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
})

const numberPart = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) => {
  const value = parts.find((part) => part.type === type)?.value
  const parsed = Number(value)

  return Number.isInteger(parsed) ? parsed : null
}

const berlinLocalDateTimeToUtcDate = ({
  year,
  month,
  day,
  hour,
  minute,
}: ParsedScheduleLabelStart) => {
  const targetUtc = Date.UTC(year, month - 1, day, hour, minute)
  let candidateUtc = targetUtc

  for (let index = 0; index < 3; index += 1) {
    const parts = berlinDateTimePartsFormatter.formatToParts(new Date(candidateUtc))
    const renderedUtc = Date.UTC(
      numberPart(parts, "year") ?? year,
      (numberPart(parts, "month") ?? month) - 1,
      numberPart(parts, "day") ?? day,
      numberPart(parts, "hour") ?? hour,
      numberPart(parts, "minute") ?? minute,
    )
    candidateUtc -= renderedUtc - targetUtc
  }

  const date = new Date(candidateUtc)

  return Number.isNaN(date.getTime()) ? null : date
}

type ParsedScheduleLabelStart = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

const parseScheduleLabelStart = (label: string): ParsedScheduleLabelStart | null => {
  const match = label.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})/)

  if (!match) return null

  const [, day, month, year, hour, minute] = match
  const parsed = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  }

  if (
    !Number.isInteger(parsed.year) ||
    !Number.isInteger(parsed.month) ||
    !Number.isInteger(parsed.day) ||
    !Number.isInteger(parsed.hour) ||
    !Number.isInteger(parsed.minute)
  ) {
    return null
  }

  return parsed
}

const parseScheduleLabelAppointmentTime = (
  label: string,
  durationMinutes?: number | null,
): AppointmentTimeTagInput | null => {
  const match = normalizeText(label).match(
    /(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})(?:\s*(?:-|–|bis)\s*(?:(\d{1,2})\.(\d{1,2})\.(\d{4})\s+)?(\d{1,2}):(\d{2}))?/,
  )

  if (!match) return null

  const [
    ,
    startDay,
    startMonth,
    startYear,
    startHour,
    startMinute,
    endDay,
    endMonth,
    endYear,
    endHour,
    endMinute,
  ] = match
  const start = berlinLocalDateTimeToUtcDate({
    year: Number(startYear),
    month: Number(startMonth),
    day: Number(startDay),
    hour: Number(startHour),
    minute: Number(startMinute),
  })

  if (!start) return null

  if (!endHour || !endMinute) {
    return durationMinutes
      ? {
          start_at: start.toISOString(),
          end_at: new Date(start.getTime() + durationMinutes * 60000).toISOString(),
          estimated_duration_min: durationMinutes,
        }
      : { start_at: start.toISOString() }
  }

  const end = berlinLocalDateTimeToUtcDate({
    year: Number(endYear ?? startYear),
    month: Number(endMonth ?? startMonth),
    day: Number(endDay ?? startDay),
    hour: Number(endHour),
    minute: Number(endMinute),
  })

  if (!end) {
    return durationMinutes
      ? {
          start_at: start.toISOString(),
          end_at: new Date(start.getTime() + durationMinutes * 60000).toISOString(),
          estimated_duration_min: durationMinutes,
        }
      : { start_at: start.toISOString() }
  }

  const normalizedEnd = end <= start ? new Date(end.getTime() + 24 * 60 * 60000) : end

  if (durationMinutes) {
    return {
      start_at: start.toISOString(),
      end_at: new Date(start.getTime() + durationMinutes * 60000).toISOString(),
      customer_window_start_at: start.toISOString(),
      customer_window_end_at: normalizedEnd.toISOString(),
      estimated_duration_min: durationMinutes,
    }
  }

  return {
    start_at: start.toISOString(),
    end_at: normalizedEnd.toISOString(),
  }
}

const pad2 = (value: number) => `${value}`.padStart(2, "0")

const formatScheduleRangeFromLabelStart = (
  start: ParsedScheduleLabelStart,
  durationMinutes: number,
) => {
  const end = new Date(
    Date.UTC(start.year, start.month - 1, start.day, start.hour, start.minute + durationMinutes),
  )
  const startDate = `${pad2(start.day)}.${pad2(start.month)}.${start.year}`
  const endDate = `${pad2(end.getUTCDate())}.${pad2(end.getUTCMonth() + 1)}.${end.getUTCFullYear()}`
  const startTime = `${pad2(start.hour)}:${pad2(start.minute)}`
  const endTime = `${pad2(end.getUTCHours())}:${pad2(end.getUTCMinutes())}`

  return endDate === startDate
    ? `${startDate} ${startTime} - ${endTime}`
    : `${startDate} ${startTime} - ${endDate} ${endTime}`
}

const previewScheduleLabel = (
  row: WorkOrderAssignmentRow,
  selection: WorkOrderAssignmentSelection,
) => {
  const durationMinutes = numberValue(selection.workOrder.estimated_duration_min)

  if (!durationMinutes) return row.scheduleLabel

  const start = scheduleStart(row)

  if (!start) {
    const labelStart = parseScheduleLabelStart(row.scheduleLabel)

    return labelStart
      ? formatScheduleRangeFromLabelStart(labelStart, durationMinutes)
      : row.scheduleLabel
  }

  const end = new Date(start.getTime() + durationMinutes * 60000)

  return formatScheduleRange(start, end)
}

const previewAppointmentTime = (
  row: WorkOrderAssignmentRow,
  selection: WorkOrderAssignmentSelection,
): AppointmentTimeTagInput | null => {
  const durationMinutes = numberValue(selection.workOrder.estimated_duration_min)
  const labelAppointmentTime = parseScheduleLabelAppointmentTime(row.scheduleLabel, durationMinutes)
  const baseAppointmentTime = row.appointmentTime
    ? {
        ...labelAppointmentTime,
        ...row.appointmentTime,
        customer_window_start_at:
          row.appointmentTime.customer_window_start_at ??
          labelAppointmentTime?.customer_window_start_at,
        customer_window_end_at:
          row.appointmentTime.customer_window_end_at ??
          labelAppointmentTime?.customer_window_end_at,
        estimated_duration_min:
          row.appointmentTime.estimated_duration_min ??
          labelAppointmentTime?.estimated_duration_min,
      }
    : labelAppointmentTime

  if (!durationMinutes) return baseAppointmentTime

  const start = scheduleStart(row) ?? parseDate(baseAppointmentTime?.start_at)

  if (!start) return baseAppointmentTime

  return {
    ...(baseAppointmentTime ?? {}),
    start_at: baseAppointmentTime?.start_at ?? start.toISOString(),
    end_at:
      baseAppointmentTime?.end_at ??
      new Date(start.getTime() + durationMinutes * 60000).toISOString(),
    estimated_duration_min: baseAppointmentTime?.estimated_duration_min ?? durationMinutes,
  }
}

const proposalDurationMinutes = (
  row: WorkOrderAssignmentRow,
  selection?: WorkOrderAssignmentSelection | null,
) => {
  const appointmentRequest = objectValue(row.payload.appointment_request)
  const appointment = objectValue(row.payload.appointment)
  const requestedAppointment = objectValue(row.payload.requested_appointment)
  const workOrder = objectValue(row.payload.work_order)
  const createWorkOrder = objectValue(row.payload.create_work_order)

  return (
    numberValue(selection?.workOrder?.estimated_duration_min) ??
    numberValue(row.payload.estimated_duration_min) ??
    numberValue(row.payload.duration_minutes) ??
    numberValue(appointmentRequest.estimated_duration_min) ??
    numberValue(appointmentRequest.duration_minutes) ??
    numberValue(appointment.estimated_duration_min) ??
    numberValue(appointment.duration_minutes) ??
    numberValue(requestedAppointment.estimated_duration_min) ??
    numberValue(requestedAppointment.duration_minutes) ??
    numberValue(workOrder.estimated_duration_min) ??
    numberValue(createWorkOrder.estimated_duration_min) ??
    numberValue(row.appointmentTime?.estimated_duration_min) ??
    numberValue(row.appointmentTime?.duration_minutes)
  )
}

const proposalScheduleLabel = (row: WorkOrderAssignmentRow) => {
  const appointmentRequest = objectValue(row.payload.appointment_request)

  return firstText(
    appointmentRequest.schedule_label,
    appointmentRequest.requested_time_label,
    row.payload.schedule_label,
    row.payload.requested_time_label,
    row.payload.appointment_label,
  )
}

const proposalAppointmentTime = (
  row: WorkOrderAssignmentRow,
  selection?: WorkOrderAssignmentSelection | null,
): AppointmentTimeTagInput | null => {
  const appointmentRequest = objectValue(row.payload.appointment_request)
  const appointment = objectValue(row.payload.appointment)
  const requestedAppointment = objectValue(row.payload.requested_appointment)
  const scheduleEvent = objectValue(row.payload.schedule_event)
  const slot = objectValue(row.payload.slot)
  const durationMinutes = proposalDurationMinutes(row, selection)
  const labelAppointmentTime = parseScheduleLabelAppointmentTime(
    proposalScheduleLabel(row),
    durationMinutes,
  )
  const actualStartAt = firstText(
    appointmentRequest.slot_start_at,
    row.payload.slot_start_at,
    appointment.slot_start_at,
    appointment.start_at,
    requestedAppointment.slot_start_at,
    requestedAppointment.start_at,
    row.payload.schedule_start_at,
    row.payload.start_at,
    scheduleEvent.start_at,
    slot.start_at,
    row.appointmentTime?.start_at,
    labelAppointmentTime?.start_at,
  )
  const actualEndAt = firstText(
    appointmentRequest.slot_end_at,
    row.payload.slot_end_at,
    appointment.slot_end_at,
    appointment.end_at,
    requestedAppointment.slot_end_at,
    requestedAppointment.end_at,
    row.payload.schedule_end_at,
    row.payload.end_at,
    scheduleEvent.end_at,
    slot.end_at,
    row.appointmentTime?.end_at,
  )
  const actualStart = parseDate(actualStartAt)
  const computedEndAt =
    actualStart && durationMinutes
      ? new Date(actualStart.getTime() + durationMinutes * 60000).toISOString()
      : ""
  const customerWindowStartAt =
    firstText(
      appointmentRequest.customer_window_start_at,
      row.payload.customer_window_start_at,
      appointment.customer_window_start_at,
      requestedAppointment.customer_window_start_at,
      scheduleEvent.customer_window_start_at,
      slot.customer_window_start_at,
      row.appointmentTime?.customer_window_start_at,
    ) ||
    labelAppointmentTime?.customer_window_start_at ||
    null
  const customerWindowEndAt =
    firstText(
      appointmentRequest.customer_window_end_at,
      row.payload.customer_window_end_at,
      appointment.customer_window_end_at,
      requestedAppointment.customer_window_end_at,
      scheduleEvent.customer_window_end_at,
      slot.customer_window_end_at,
      row.appointmentTime?.customer_window_end_at,
    ) ||
    labelAppointmentTime?.customer_window_end_at ||
    null
  const result: AppointmentTimeTagInput = {
    ...(row.appointmentTime ?? {}),
    start_at:
      actualStartAt || row.appointmentTime?.start_at || labelAppointmentTime?.start_at || null,
    end_at:
      actualEndAt ||
      computedEndAt ||
      row.appointmentTime?.end_at ||
      labelAppointmentTime?.end_at ||
      null,
    flex_type:
      firstText(
        appointmentRequest.flex_type,
        appointment.flex_type,
        requestedAppointment.flex_type,
        row.payload.flex_type,
        row.appointmentTime?.flex_type,
      ) || null,
    timeframe_start_at:
      firstText(
        appointmentRequest.timeframe_start_at,
        appointment.timeframe_start_at,
        requestedAppointment.timeframe_start_at,
        row.payload.timeframe_start_at,
        row.appointmentTime?.timeframe_start_at,
      ) || null,
    timeframe_end_at:
      firstText(
        appointmentRequest.timeframe_end_at,
        appointment.timeframe_end_at,
        requestedAppointment.timeframe_end_at,
        row.payload.timeframe_end_at,
        row.appointmentTime?.timeframe_end_at,
      ) || null,
    customer_window_start_at: customerWindowStartAt,
    customer_window_end_at: customerWindowEndAt,
    estimated_duration_min:
      durationMinutes ??
      row.appointmentTime?.estimated_duration_min ??
      labelAppointmentTime?.estimated_duration_min ??
      null,
    duration_minutes:
      row.appointmentTime?.duration_minutes ?? labelAppointmentTime?.duration_minutes ?? null,
  }

  return result.start_at ||
    result.end_at ||
    result.timeframe_start_at ||
    result.timeframe_end_at ||
    result.customer_window_start_at ||
    result.customer_window_end_at
    ? result
    : null
}

const eventAppointmentTime = (
  event?: ScheduleEvent | null,
  durationMinutes?: number | null,
): AppointmentTimeTagInput | null => {
  if (!event) return null

  const eventTiming = event as ScheduleEvent & {
    customer_window_start_at?: string | null
    customer_window_end_at?: string | null
    estimated_duration_min?: number | string | null
    duration_minutes?: number | string | null
  }

  return {
    start_at: event.start_at,
    end_at: event.end_at,
    flex_type: event.flex_type,
    timeframe_start_at: event.timeframe_start_at,
    timeframe_end_at: event.timeframe_end_at,
    customer_window_start_at: eventTiming.customer_window_start_at,
    customer_window_end_at: eventTiming.customer_window_end_at,
    estimated_duration_min: eventTiming.estimated_duration_min ?? durationMinutes ?? null,
    duration_minutes: eventTiming.duration_minutes ?? null,
  }
}

const scheduleEventStatus = (event?: ScheduleEvent | null): ScheduleEventStatus | null => {
  const status = normalizeText(event?.status).toUpperCase()

  return status ? (status as ScheduleEventStatus) : null
}

const employeeLabelFromEvent = (event?: ScheduleEvent | null) =>
  normalizeText(
    (event as (ScheduleEvent & { employee_name?: string | null }) | null | undefined)
      ?.employee_name,
  )

const onsiteContactNameFromEvent = (event?: ScheduleEvent | null) =>
  normalizeText(event?.onsite_contact?.display_name ?? event?.appointment_contact?.contact_name)

const appointmentTooltip = ({
  title,
  time,
  employeeLabel,
  contactName,
  origin,
  emptyText,
}: {
  title: string
  time?: AppointmentTimeTagInput | null
  employeeLabel?: string | null
  contactName?: string | null
  origin: string
  emptyText?: string | null
}) => {
  const presentation = buildAppointmentTimeTagPresentation(time)

  return buildRichTooltip([
    tooltipTitle(title),
    emptyText ? tooltipText(emptyText) : tooltipField("Zeit", presentation.label),
    presentation.timeWindowLabel
      ? tooltipField(
          presentation.usesCustomerWindow ? "Kunden-Zeitfenster" : "Flex-Zeitfenster",
          presentation.timeWindowLabel,
        )
      : null,
    presentation.usesCustomerWindow
      ? tooltipField("Kalendertermin", presentation.scheduledPeriodLabel)
      : null,
    presentation.isFlex || presentation.usesCustomerWindow
      ? tooltipField("Geplante Dauer", presentation.durationLabel)
      : null,
    presentation.tooltipDescription ? tooltipText(presentation.tooltipDescription) : null,
    tooltipField("Mitarbeiter", employeeLabel),
    tooltipField("Ansprechpartner", contactName),
    tooltipField("Herkunft", origin),
  ])
}

const appointmentStatus = ({
  value,
  label,
  color = "blue",
  icon = mdiCalendarClockOutline,
  description,
}: {
  value: string
  label: string
  color?: StatusDefinition<string>["color"]
  icon?: string
  description?: string
}): StatusDefinition<string> => ({
  value,
  label,
  color,
  icon,
  description,
})

type AppointmentTagDisplay = {
  status: StatusDefinition<string> | null
  subLabel: string | null
  tooltipContent: string | null
  fallbackLabel?: string | null
}

const requestedAppointmentTime = (row: WorkOrderAssignmentRow, draft: AssignWorkOrderDraft) => {
  const selection = selectedOverride(row, draft) ?? draft.selectedWorkOrder

  return (
    proposalAppointmentTime(row, selection) ??
    row.appointmentTime ??
    parseScheduleLabelAppointmentTime(row.scheduleLabel)
  )
}

type AppointmentComparableRange = {
  start: Date
  end: Date | null
}

const comparableRangeFromAppointmentTime = (
  time?: AppointmentTimeTagInput | null,
): AppointmentComparableRange | null => {
  const start = parseDate(time?.start_at)

  if (!start) return null

  return {
    start,
    end: parseDate(time?.end_at),
  }
}

const rangesOverlapOrMatch = (
  requested: AppointmentComparableRange | null,
  existing: AppointmentComparableRange | null,
) => {
  if (!requested || !existing) return false

  if (requested.end && existing.end) {
    return requested.start < existing.end && existing.start < requested.end
  }

  return requested.start.getTime() === existing.start.getTime()
}

const requestedEmployeeLabel = (row: WorkOrderAssignmentRow) => {
  const targetEmployeeLabel = normalizeText(row.targetEmployeeLabel)

  return targetEmployeeLabel && targetEmployeeLabel !== "—"
    ? targetEmployeeLabel
    : row.employeeLabel
}

const changedMeta = (
  initialValue: string,
  initialSource: WorkOrderAssignmentOriginSource,
): WorkOrderAssignmentFieldOriginMeta => ({
  initialValue,
  initialSource: initialValue ? initialSource : "none",
  currentSource: "work_order",
})

const candidateRecommendationLabel = (candidate: WorkOrderAssignmentCandidate) => {
  const orderType = compactOrderTypeLabel(normalizeText(candidate.orderType))
  const orderNumber = orderNumberLabel(candidate.orderNumber)

  if (orderType || orderNumber)
    return [orderType || "Auftrag", orderNumber].filter(Boolean).join(" ")

  const labelWithoutDate = normalizeText(candidate.label).replace(/\s*\([^)]*\)\s*$/, "")
  const labelTail = labelWithoutDate.split(/\s+-\s+/).pop() ?? labelWithoutDate
  const normalizedTail = normalizeWhitespace(labelTail)

  return normalizedTail.replace(/\s+(\d+)$/, " #$1") || "Auftrag"
}

const candidateRecommendationNumberLabel = (candidate: WorkOrderAssignmentCandidate) =>
  orderNumberLabel(candidate.orderNumber) || candidateRecommendationLabel(candidate)

const parsedWorkOrderLabel = (value: unknown) => {
  const normalized = normalizeText(value)
  const summaryPart = normalized.split(/\s+-\s+/).pop() ?? normalized
  const dashMatch = summaryPart.match(/^\s*(?:#?(\d+)\s*[-–]\s*)?(.+?)\s*(?:#(\d+))?\s*$/)
  const numberMatch = summaryPart.match(/#\s*(\d+)\b/) ?? summaryPart.match(/^\s*(\d+)\s*$/)
  const rawType = normalizeText(dashMatch?.[2])
    .replace(/#?\b\d+\b/g, "")
    .trim()

  return {
    orderNumber: normalizeText(dashMatch?.[1] ?? dashMatch?.[3] ?? numberMatch?.[1]),
    orderTypeLabel: rawType && rawType !== summaryPart ? rawType : "",
  }
}

const workOrderSummaryWorkOrder = ({
  id,
  orderNumber,
  orderTypeLabel,
  orderTypes,
  dueDate,
  propertyId,
  propertyUnitId,
  unitLabel,
}: {
  id: string
  orderNumber: string
  orderTypeLabel: string
  orderTypes?: WorkOrderOrderTypeItem[]
  dueDate: string
  propertyId?: string | null
  propertyUnitId?: string | null
  unitLabel?: string | null
}): WorkOrder =>
  ({
    id,
    order_number: orderNumber || null,
    order_types: orderTypes?.length
      ? orderTypes
      : orderTypeLabel
        ? [
            {
              order_type_id: null,
              internal_label: orderTypeLabel,
              public_label: orderTypeLabel,
              description: null,
            },
          ]
        : [],
    due_date: dueDate || null,
    property_id: propertyId ?? null,
    property_unit_id: propertyUnitId ?? null,
    property_unit_label: unitLabel || null,
  }) as unknown as WorkOrder

const workOrderSummaryDisplayFromParts = ({
  id,
  label,
  orderNumber,
  orderTypeLabel,
  orderTypes,
  dueDate,
  propertyId,
  propertyUnitId,
  unitLabel,
}: {
  id: string
  label?: string | null
  orderNumber?: string | null
  orderTypeLabel?: string | null
  orderTypes?: WorkOrderOrderTypeItem[]
  dueDate?: string | null
  propertyId?: string | null
  propertyUnitId?: string | null
  unitLabel?: string | null
}) => {
  const parsed = parsedWorkOrderLabel(label)
  const resolvedOrderNumber = normalizeText(orderNumber) || parsed.orderNumber
  const resolvedOrderTypeLabel =
    normalizeText(orderTypeLabel) || parsed.orderTypeLabel || (resolvedOrderNumber ? "Auftrag" : "")
  const resolvedDueDate = normalizeText(dueDate)
  const hasSummary = Boolean(id || resolvedOrderNumber || resolvedOrderTypeLabel || resolvedDueDate)

  return {
    workOrder: hasSummary
      ? workOrderSummaryWorkOrder({
          id,
          orderNumber: resolvedOrderNumber,
          orderTypeLabel: resolvedOrderTypeLabel,
          orderTypes,
          dueDate: resolvedDueDate,
          propertyId,
          propertyUnitId,
          unitLabel,
        })
      : null,
    label: resolvedOrderTypeLabel || normalizeText(label),
    subLabel: resolvedDueDate ? `fällig: ${formatWorkOrderDueDate(resolvedDueDate)}` : "",
    orderNumber: resolvedOrderNumber,
    orderTypeLabel: resolvedOrderTypeLabel,
  }
}

const orderTypeLabelFromWorkOrder = (workOrder: Record<string, unknown>) => {
  const fromItems = internalOrderTypeLabel(workOrder as { order_types?: WorkOrderOrderTypeItem[] })
  if (fromItems) return fromItems

  return normalizeText(workOrder.order_type_label ?? workOrder.order_type)
}

const rowPayloadWorkOrderSummary = (row: WorkOrderAssignmentRow) => {
  const workOrder = objectValue(row.payload.work_order)
  const parsed = parsedWorkOrderLabel(row.workOrderLabel)
  const orderTypeLabel = normalizeText(
    orderTypeLabelFromWorkOrder(workOrder) ||
      row.payload.order_type_label ||
      row.payload.order_type ||
      parsed.orderTypeLabel,
  )
  const dueDate = normalizeText(workOrder.due_date ?? row.payload.due_date)

  return {
    orderNumber:
      normalizeText(workOrder.order_number ?? row.payload.order_number) || parsed.orderNumber,
    orderTypeLabel,
    dueDate,
    subLabel: dueDate ? `fällig: ${formatWorkOrderDueDate(dueDate)}` : "",
  }
}

export const workOrderAssignmentSummaryDisplay = (
  row: WorkOrderAssignmentRow,
  draft: Partial<AssignWorkOrderDraft> = {},
) => {
  const payloadSummary = rowPayloadWorkOrderSummary(row)

  if (draft.selectedWorkOrder?.workOrder) {
    const workOrder = objectValue(draft.selectedWorkOrder.workOrder)

    return workOrderSummaryDisplayFromParts({
      id: normalizeText(workOrder.id ?? draft.selectedWorkOrder.id ?? row.workOrderId),
      label: draft.selectedWorkOrder.label || row.workOrderLabel,
      orderNumber: normalizeText(workOrder.order_number) || payloadSummary.orderNumber,
      orderTypeLabel: orderTypeLabelFromWorkOrder(workOrder) || payloadSummary.orderTypeLabel,
      orderTypes: draft.selectedWorkOrder.workOrder.order_types,
      dueDate: normalizeText(workOrder.due_date) || payloadSummary.dueDate,
      propertyId: normalizeText(workOrder.property_id) || row.propertyId,
      propertyUnitId: normalizeText(workOrder.property_unit_id) || row.propertyUnitId,
      unitLabel:
        normalizeText(workOrder.property_unit_label) || row.propertyUnitLabel || row.unitLabel,
    })
  }

  const candidate = row.workOrderId
    ? row.candidates.find((entry) => entry.id === row.workOrderId)
    : null

  if (candidate) {
    return workOrderSummaryDisplayFromParts({
      id: candidate.id,
      label: candidate.label || row.workOrderLabel,
      orderNumber: candidate.orderNumber || payloadSummary.orderNumber,
      orderTypeLabel: candidate.orderType || payloadSummary.orderTypeLabel,
      orderTypes: candidate.selection?.workOrder?.order_types,
      dueDate: candidate.dueDate || payloadSummary.dueDate,
      propertyId: candidate.propertyId ?? row.propertyId,
      propertyUnitId: candidate.propertyUnitId ?? row.propertyUnitId,
      unitLabel: candidate.unitLabel || row.propertyUnitLabel || row.unitLabel,
    })
  }

  return workOrderSummaryDisplayFromParts({
    id: row.workOrderId ?? "",
    label: row.workOrderId ? row.workOrderLabel : "",
    orderNumber: payloadSummary.orderNumber,
    orderTypeLabel: payloadSummary.orderTypeLabel,
    dueDate: payloadSummary.dueDate,
    propertyId: row.propertyId,
    propertyUnitId: row.propertyUnitId,
    unitLabel: row.propertyUnitLabel || row.unitLabel,
  })
}

export const workOrderAssignmentRecommendationText = (row: WorkOrderAssignmentRow) => {
  const candidates = recommendedWorkOrderCandidates(row)
  const labels = candidates
    .map(candidates.length > 1 ? candidateRecommendationNumberLabel : candidateRecommendationLabel)
    .filter(Boolean)

  if (!labels.length) return "Empfohlen: Auftrag anlegen (Plus in dieser Zeile drücken)"

  return `Empfohlen: ${labels.join(", ")}`
}

export const assignWorkOrderDraftHasChanges = (
  row: WorkOrderAssignmentRow,
  draft: AssignWorkOrderDraft,
) => normalizeText(draft.workOrderId) !== normalizeText(row.workOrderId)

export const workOrderAssignmentScheduleDisplay = (
  row: WorkOrderAssignmentRow,
  draft: AssignWorkOrderDraft,
) => {
  const selection = selectedOverride(row, draft)

  if (!selection)
    return { label: row.scheduleLabel, appointmentTime: row.appointmentTime, meta: null }

  const appointmentTime = previewAppointmentTime(row, selection)
  const appointmentLabel = buildAppointmentTimeTagPresentation(appointmentTime).label

  return {
    label:
      appointmentTime && appointmentLabel !== "Kein Termin"
        ? appointmentLabel
        : previewScheduleLabel(row, selection),
    appointmentTime,
    meta: changedMeta(row.scheduleLabel, "requester_appointment"),
  }
}

export const workOrderAssignmentRequestedAppointmentDisplay = (
  row: WorkOrderAssignmentRow,
  draft: AssignWorkOrderDraft,
) => {
  const appointmentTime = requestedAppointmentTime(row, draft)
  const presentation = buildAppointmentTimeTagPresentation(appointmentTime)
  const label =
    presentation.label && presentation.label !== "Kein Termin"
      ? presentation.label
      : row.scheduleLabel

  return {
    status: appointmentStatus({
      value: "requested_appointment",
      label: label || "Terminwunsch",
      color: "blue",
      description: "Vom Anfragenden gewünschter Termin.",
    }),
    subLabel: buildAppointmentSubLabel({
      employeeLabel: requestedEmployeeLabel(row),
      contactName: row.customerLabel,
      prefixEmployeeOnly: true,
    }),
    tooltipContent: appointmentTooltip({
      title: "Terminwunsch",
      time: appointmentTime,
      employeeLabel: requestedEmployeeLabel(row),
      contactName: row.customerLabel,
      origin: "aus Terminwunsch-Rückmeldung",
    }),
  }
}

export const workOrderAssignmentExistingAppointmentDisplay = (
  row: WorkOrderAssignmentRow,
  draft: AssignWorkOrderDraft,
): AppointmentTagDisplay => {
  const selectedWorkOrderId = normalizeText(draft.workOrderId)

  if (!selectedWorkOrderId) {
    return {
      status: null,
      subLabel: null,
      tooltipContent: null,
      fallbackLabel: "—",
    }
  }

  const selected = draft.selectedWorkOrder

  if (!selected) {
    return {
      status: appointmentStatus({
        value: "loading_existing_appointment",
        label: "Termin wird geprüft",
        color: "blue",
        icon: mdiCalendarQuestionOutline,
        description: "Der aktuelle Auftragstermin wird geladen.",
      }),
      subLabel: null,
      tooltipContent: appointmentTooltip({
        title: "Bisheriger Termin",
        origin: "aus ausgewähltem Auftrag",
        emptyText: "Der aktuelle Termin des ausgewählten Auftrags wird noch geprüft.",
      }),
    }
  }

  const canceledEvent =
    row.isCancellation &&
    normalizeText(row.canceledScheduleEvent?.work_order_id) === selectedWorkOrderId
      ? row.canceledScheduleEvent
      : null
  const selectedEvent = canceledEvent ?? selected.currentScheduleEvent
  const selectedDurationMinutes = numberValue(selected?.workOrder?.estimated_duration_min)
  const eventTime = eventAppointmentTime(
    selectedEvent,
    canceledEvent ? null : selectedDurationMinutes,
  )

  if (!eventTime && selected.currentScheduleEvent === undefined && !canceledEvent) {
    return {
      status: appointmentStatus({
        value: "loading_existing_appointment",
        label: "Termin wird geprüft",
        color: "blue",
        icon: mdiCalendarQuestionOutline,
        description: "Der aktuelle Auftragstermin wird geladen.",
      }),
      subLabel: null,
      tooltipContent: appointmentTooltip({
        title: "Bisheriger Termin",
        origin: "aus ausgewähltem Auftrag",
        emptyText: "Der aktuelle Termin des ausgewählten Auftrags wird noch geprüft.",
      }),
    }
  }

  if (!eventTime) {
    return {
      status: appointmentStatus({
        value: "no_existing_appointment",
        label: "Kein bisheriger Termin",
        color: "blue",
        icon: mdiCalendarQuestionOutline,
        description: "Zum ausgewählten Auftrag ist kein aktueller Termin hinterlegt.",
      }),
      subLabel: null,
      tooltipContent: appointmentTooltip({
        title: "Bisheriger Termin",
        origin: selected ? "aus ausgewähltem Auftrag" : "noch kein Auftrag ausgewählt",
        emptyText: "Für diese Zeile wurde kein bestehender Termin gefunden.",
      }),
    }
  }

  const presentation = buildAppointmentTimeTagPresentation(eventTime)
  const eventStatus = canceledEvent
    ? appointmentStatus({
        value: "cancel_target_appointment",
        label: presentation.label,
        color: "rose",
        description: "Termin, auf den sich die Absage bezieht.",
      })
    : {
        ...buildScheduleEventCommunicationTagPresentation({
          status: scheduleEventStatus(selectedEvent) ?? "UNKNOWN",
          event: selectedEvent as ScheduleEvent | null,
        }).definition,
        label: presentation.label,
      }
  const communication = buildScheduleEventCommunicationTagPresentation({
    status: scheduleEventStatus(selectedEvent) ?? "UNKNOWN",
    event: selectedEvent as ScheduleEvent | null,
  })
  const employeeLabel = employeeLabelFromEvent(selectedEvent) || requestedEmployeeLabel(row)
  const contactName = onsiteContactNameFromEvent(selectedEvent) || row.customerLabel

  return {
    status: eventStatus,
    subLabel: communication.label,
    tooltipContent:
      communication.tooltip +
      appointmentTooltip({
        title: row.isCancellation ? "Zu stornierender Termin" : "Bisheriger Termin",
        time: eventTime,
        employeeLabel,
        contactName,
        origin: canceledEvent ? "aus Storno-Rückmeldung" : "aus ausgewähltem Auftrag",
      }),
  }
}

export const workOrderAssignmentAppointmentComparisonDisplay = (
  row: WorkOrderAssignmentRow,
  draft: AssignWorkOrderDraft,
): AppointmentTagDisplay => {
  if (row.isCancellation) {
    return {
      status: null,
      fallbackLabel: "Ohne Vorschlag",
      subLabel: null,
      tooltipContent: buildRichTooltip([
        tooltipTitle("Terminvorschlag"),
        tooltipText("Diese Rückmeldung ist eine Terminabsage und enthält keinen Terminvorschlag."),
      ]),
    }
  }

  const requestedTime = requestedAppointmentTime(row, draft)
  const requestedPresentation = buildAppointmentTimeTagPresentation(requestedTime)
  const requestedLabel =
    requestedPresentation.label && requestedPresentation.label !== "Kein Termin"
      ? requestedPresentation.label
      : row.scheduleLabel || "Terminwunsch"
  const selected = draft.selectedWorkOrder
  const selectedEvent = selected?.currentScheduleEvent
  const existingTime = eventAppointmentTime(
    selectedEvent,
    numberValue(selected?.workOrder?.estimated_duration_min),
  )
  const nextEmployee = requestedEmployeeLabel(row)
  const requestedSubLabel = buildAppointmentSubLabel({
    employeeLabel: nextEmployee,
    contactName: row.customerLabel,
    prefixEmployeeOnly: true,
  })

  if (!existingTime && selected && selected.currentScheduleEvent === undefined) {
    return {
      status: appointmentStatus({
        value: "requested_existing_loading",
        label: requestedLabel,
        color: "blue",
        description: "Der aktuelle Auftragstermin wird noch geprüft.",
      }),
      subLabel: ["Terminwunsch · noch nicht bestätigt", requestedSubLabel]
        .filter(Boolean)
        .join(" · "),
      tooltipContent: buildRichTooltip([
        tooltipTitle("Terminwunsch und bisheriger Termin"),
        tooltipField("Terminwunsch", requestedLabel),
        tooltipField("Bisheriger Termin", "wird geprüft"),
        tooltipText("Der bisherige Termin des ausgewählten Auftrags wird noch geladen."),
      ]),
    }
  }

  const existingPresentation = buildAppointmentTimeTagPresentation(existingTime)
  const existingLabel =
    existingTime && existingPresentation.label !== "Kein Termin"
      ? existingPresentation.label
      : "terminlos"
  const rangesMatch = rangesOverlapOrMatch(
    comparableRangeFromAppointmentTime(requestedTime),
    comparableRangeFromAppointmentTime(existingTime),
  )
  const previousEmployee = employeeLabelFromEvent(selectedEvent)
  const employeeChanged =
    row.employeeChanged ||
    Boolean(previousEmployee && nextEmployee && previousEmployee !== nextEmployee)
  const matches = Boolean(existingTime && rangesMatch && !employeeChanged)
  const timeAssessment = existingTime
    ? rangesMatch
      ? "Terminwunsch und bisheriger Termin überschneiden sich."
      : "Terminwunsch und bisheriger Termin liegen auseinander."
    : "Zum ausgewählten Auftrag ist kein bisheriger Termin hinterlegt."
  const employeeAssessment = employeeChanged
    ? "Der Mitarbeiter unterscheidet sich vom bisherigen Termin."
    : "Der Mitarbeiter bleibt gleich oder war bisher nicht hinterlegt."

  return {
    status: appointmentStatus({
      value: matches ? "requested_matches_existing" : "requested_differs_existing",
      label: requestedLabel,
      color: matches ? "neutral" : "orange",
      description: matches
        ? "Terminwunsch passt zum bisherigen Termin."
        : "Terminwunsch unterscheidet sich vom bisherigen Termin oder Mitarbeiter.",
    }),
    subLabel: ["Terminwunsch · noch nicht bestätigt", requestedSubLabel]
      .filter(Boolean)
      .join(" · "),
    tooltipContent: buildRichTooltip([
      tooltipTitle("Terminwunsch prüfen"),
      tooltipField("Terminwunsch", requestedLabel),
      requestedPresentation.timeWindowLabel
        ? tooltipField(
            requestedPresentation.usesCustomerWindow ? "Kunden-Zeitfenster" : "Flex-Zeitfenster",
            requestedPresentation.timeWindowLabel,
          )
        : null,
      requestedPresentation.usesCustomerWindow
        ? tooltipField("Kalendertermin", requestedPresentation.scheduledPeriodLabel)
        : null,
      tooltipField("Bisheriger Termin", existingLabel),
      tooltipField("Zeitbewertung", timeAssessment),
      tooltipField("Mitarbeiter neu", nextEmployee),
      tooltipField("Mitarbeiter bisher", previousEmployee),
      tooltipField("Mitarbeiterbewertung", employeeAssessment),
      tooltipText(
        matches
          ? "Der Tag ist grün, weil Zeit und Mitarbeiter zum bisherigen Termin passen."
          : "Der Tag ist rot, weil Zeit, Mitarbeiter oder der bisherige Termin geprüft werden müssen.",
      ),
    ]),
  }
}

export const workOrderAssignmentPropertyDisplay = (
  row: WorkOrderAssignmentRow,
  draft: AssignWorkOrderDraft,
) => {
  const selection = selectedOverride(row, draft)
  const current = selection ?? draft.selectedWorkOrder

  if (!current) {
    const tag = buildPropertyObjectTagPresentation({
      property: null,
      propertyUnitId: row.propertyUnitId,
      fallbackAddressLabel: row.addressLabel,
      fallbackUnitLabel: row.propertyUnitLabel || row.unitLabel,
      mode: "address-first",
    })

    return {
      tag,
      tooltipContent: buildRichTooltip([
        tooltipTitle("Liegenschaft"),
        tooltipField("Adresse", row.addressLabel),
        tooltipField("Einheit", row.propertyUnitLabel || row.unitLabel),
        tooltipField("Herkunft", "aus Rückmeldung oder Zuordnungslogik"),
      ]),
      hints: row.propertyHints,
      meta: null,
    }
  }

  const tag = buildPropertyObjectTagPresentation({
    property: null,
    propertyUnitId: current.propertyUnitId,
    fallbackAddressLabel: current.propertyLabel || row.addressLabel,
    fallbackUnitLabel: current.unitLabel || row.propertyUnitLabel || row.unitLabel,
    mode: "address-first",
  })

  return {
    tag,
    tooltipContent: buildRichTooltip([
      tooltipTitle("Liegenschaft des zugeordneten Auftrags"),
      tooltipField("Adresse", current.propertyLabel || row.addressLabel),
      tooltipField("Einheit", current.unitLabel || row.propertyUnitLabel || row.unitLabel),
      tooltipField(
        "Herkunft",
        selection ? "aus manuell ausgewähltem Auftrag" : "aus vorgeschlagenem Auftrag",
      ),
    ]),
    hints: row.propertyHints,
    meta: selection ? changedMeta(row.addressLabel, "requester_selection") : null,
  }
}

export const workOrderAssignmentUnitDisplay = (
  row: WorkOrderAssignmentRow,
  draft: AssignWorkOrderDraft,
) => {
  const selection = selectedOverride(row, draft)

  if (!selection) return { label: row.unitContextLabel || "Nicht genannt", meta: null }

  return {
    label: selection.unitLabel || "Nicht genannt",
    meta: changedMeta(row.unitContextLabel || "Nicht genannt", "requester_selection"),
  }
}
